import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Send, Eye, Loader2, Radio } from "lucide-react";
import { toast } from "sonner";

export const DispatchLog = () => {
  const [logs, setLogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/dispatch-log").then(({ data }) => setLogs(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/admin/dispatch-preview");
      setPreview(data);
      setShowPreview(true);
    } catch {
      toast.error("Could not load the dossier preview.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString();

  return (
    <div className="mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6" data-testid="dispatch-log-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><Send size={18} className="text-[#00FF87]" /> 2K Dispatch Log</h2>
          <p className="mt-1 text-sm text-[#94A3B8]">Every dossier sent to 2K — when, how many cases, and which cheaters. Only confirmed cheaters are ever sent.</p>
        </div>
        <Button data-testid="dispatch-preview-btn" onClick={loadPreview} disabled={loadingPreview} variant="outline" size="sm"
          className="border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 hover:text-[#00F0FF]">
          {loadingPreview ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eye size={14} className="mr-1" />} Preview next dossier
        </Button>
      </div>

      {showPreview && preview && (
        <div className="mt-4 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.05] p-4" data-testid="dispatch-preview-box">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Queued to send now → <span className="text-[#00F0FF]">{preview.recipient || "no recipient set"}</span></span>
            <button onClick={() => setShowPreview(false)} className="text-xs text-[#64748B] hover:text-white">Hide</button>
          </div>
          {preview.count === 0 ? (
            <p className="mt-2 text-sm text-[#94A3B8]">No confirmed cheaters in the last 24 hours — nothing would be sent right now.</p>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-[#94A3B8]">{preview.count} case(s) would be included:</p>
              {preview.cases.map((c) => (
                <div key={c.id} className="rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-2 text-sm" data-testid={`preview-case-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-white">{c.gamertag}</span>
                    <span className="text-xs text-[#94A3B8]">{c.platform} · {c.game_mode}</span>
                    <span className="ml-auto rounded bg-[#FF2A55]/15 px-1.5 py-0.5 text-[11px] font-semibold text-[#FF6B87]">{c.cheat_type}</span>
                    <span className="text-[11px] text-[#94A3B8]">{c.faced} faced</span>
                  </div>
                  {c.verdict_note && <p className="mt-1 text-xs italic text-[#86EFAC]">Verdict: {c.verdict_note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-5">
        {logs.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[#1E293F] bg-[#0B1120] py-8 text-center text-sm text-[#64748B]" data-testid="dispatch-log-empty">
            Nothing sent to 2K yet. Confirmed cheaters go out automatically each night, or hit "Send 2K digest now."
          </p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <details key={l.id} className="rounded-lg border border-[#1E293F] bg-[#141B2D]" data-testid={`dispatch-log-${l.id}`}>
                <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2.5 text-sm">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${l.kind === "digest" ? "bg-[#00FF87]/15 text-[#00FF87]" : "bg-[#FF2A55]/15 text-[#FF2A55]"}`}>
                    {l.kind === "digest" ? "Digest" : "Single"}
                  </span>
                  <span className="font-semibold text-white">{l.count} case(s)</span>
                  <span className="text-[#64748B]">·</span>
                  <span className="text-[#94A3B8]">{fmt(l.at)}</span>
                  <span className="ml-auto text-xs text-[#64748B]">by {l.sent_by}</span>
                </summary>
                <div className="border-t border-[#1E293F] px-3 py-2">
                  {(l.cases || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-2 py-1 text-sm">
                      <span className="font-medium text-white">{c.gamertag}</span>
                      <span className="text-xs text-[#94A3B8]">{c.platform}</span>
                      <span className="ml-auto text-xs text-[#FF6B87]">{c.cheat_type}</span>
                    </div>
                  ))}
                  <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#64748B]"><Radio size={11} /> Sent to {l.recipient}</div>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
